/**
 * Configuration des graphiques pour les statistiques
 * Ce fichier centralise toutes les configurations de graphiques utilisées dans l'application
*/


  // Fonction pour calculer la moyenne des progres
  export const calculateAverageProgress = (usersProgress) => {
    if (!Array.isArray(usersProgress) || usersProgress.length === 0) {
      return 0;
    }
  
    const totalProgress = usersProgress.reduce(
      (sum, user) => sum + user.programProgress,
      0
    );
    return totalProgress / usersProgress.length || 0;
  };

  // Configuration pour le graphique de progression des etudiants
  export const getProgramProgressChart = (progressData) => {
    const averageProgress = calculateAverageProgress(progressData?.usersProgress);
    
    return {
      data: {
        labels:
          progressData?.usersProgress?.map(
            (user) => user.user.first_name + " " + user.user.last_name
          ) || [],
        datasets: [
          {
            type: "bar",
            label: "Progress %",
            data:
              progressData?.usersProgress?.map((user) => user.programProgress) ||
              [],
            backgroundColor: "rgba(54, 162, 235, 0.5)",
          },
          {
            type: "line",
            label: "Average Progress",
            data: Array(progressData?.usersProgress?.length).fill(
              averageProgress || 0
            ),
            borderColor: "rgba(255, 99, 132, 1)",
            borderWidth: 2,
            fill: false,
          },
        ],
      },
      options: {
        responsive: true,
        scales: {
          y: { 
            beginAtZero: true, 
            max: 100,
            title: {
              display: true,
              text: 'Progression (%)'
            }
          },
          x: {
            title: {
              display: true,
              text: 'Étudiants'
            }
          }
        },
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: `Progression des étudiants`,
            font: {
              size: 18
            },
            padding: {
              top: 10,
              bottom: 30
            }
          },
          legend: {
            display: false,
          }
        }
      }
    };
  };

// Configuration pour le graphique circulaire (Actif vs Inactif)
export const getActiveStudentsPieChart = (progressData) => {
  if (!progressData || !progressData.usersProgress) {
    return {
      data: {
        labels: ['Actifs', 'Inactifs'],
        datasets: [{ data: [0, 0], backgroundColor: ['#28a745', '#dc3545'] }]
      },
      options: {}
    };
  }

  // Définir un étudiant actif comme un étudiant ayant complété au moins une leçon et dont le compte est actif
  const activeUsers = progressData.usersProgress.filter(
    (user) => user.completedLessons > 0 && user.is_active
  ).length;

  const inactiveUsers = progressData.usersProgress.length - activeUsers;

  return {
    data: {
      labels: ['Étudiants actifs', 'Étudiants inactifs'],
      datasets: [
        {
          data: [activeUsers, inactiveUsers],
          backgroundColor: [
            'rgba(40, 167, 69, 0.7)',
            'rgba(220, 53, 69, 0.7)'
          ],
          borderColor: ['#28a745', '#dc3545'],
          borderWidth: 1
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: 'Étudiants actifs vs inactifs',
          font: {
            size: 18
          },
          padding: {
            top: 10,
            bottom: 15
          }
        },
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 15,
            padding: 15
          }
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.raw;
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = Math.round((value / total) * 100);
              return `${context.label}: ${value} (${percentage}%)`;
            }
          }
        },
        subtitle: {
          display: true,
          text: 'Un étudiant actif a complété au moins une leçon et son compte est actif',
          padding: {
            bottom: 15
          }
        }
      }
    }
  };
};

// Configuration pour le graphique de progression des étudiants dans un module spécifique
export const getModuleProgressChart = (progressData, moduleId) => {
  if (!progressData || !progressData.usersProgress || !moduleId) {
    return {
      data: {
        labels: [],
        datasets: []
      },
      options: {}
    };
  }

  // Extraire les données de progression pour le module spécifique
  const moduleProgressData = progressData.usersProgress.map(user => {
    // Trouver la progression du module spécifique dans l'array moduleProgress
    const moduleProgress = user.moduleProgress?.find(
      mp => mp.moduleId === moduleId || mp.moduleId?._id === moduleId
    );
    
    return {
      userName: `${user.user.first_name} ${user.user.last_name}`,
      progress: moduleProgress?.progress || 0,
      userId: user.userId || user.user._id
    };
  });

  // Calculer la moyenne de progression pour ce module
  const averageModuleProgress = moduleProgressData.length > 0
    ? moduleProgressData.reduce((sum, user) => sum + user.progress, 0) / moduleProgressData.length
    : 0;

  return {
    data: {
      labels: moduleProgressData.map(user => user.userName),
      datasets: [
        {
          type: "bar",
          label: "Progression du module (%)",
          data: moduleProgressData.map(user => user.progress),
          backgroundColor: "rgba(75, 192, 192, 0.6)",
          borderColor: "rgba(75, 192, 192, 1)",
          borderWidth: 1,
        },
        {
          type: "line",
          label: "Progression moyenne",
          data: Array(moduleProgressData.length).fill(averageModuleProgress),
          borderColor: "rgba(255, 159, 64, 1)",
          borderWidth: 2,
          borderDash: [5, 5],
          fill: false,
          pointRadius: 0,
        }
      ],
    },
    options: {
      responsive: true,
      scales: {
        y: { 
          beginAtZero: true, 
          max: 100,
          title: {
            display: true,
            text: 'Progression (%)'
          },
          ticks: {
            callback: value => `${value}%`
          }
        },
        x: {
          title: {
            display: true,
            text: 'Étudiants'
          }
        }
      },
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: `Progression des étudiants dans le module`,
          font: {
            size: 18,
            weight: 'bold'
          },
          padding: {
            top: 10,
            bottom: 20
          }
        },
        legend: {
          display: true,
          position: 'bottom'
        },
        tooltip: {
          callbacks: {
            label: context => {
              if (context.dataset.type === 'line') {
                return `Moyenne: ${Math.round(context.raw)}%`;
              }
              return `Progression: ${context.raw}%`;
            }
          }
        }
      }
    }
  };
};

// Configuration pour le graphique circulaire des statuts de complétion du module
export const getModuleCompletionStatusChart = (progressData, moduleId) => {
  if (!progressData || !progressData.usersProgress || !moduleId) {
    return {
      data: {
        labels: [],
        datasets: []
      },
      options: {}
    };
  }

  // Extraire les données de complétion du module
  const moduleData = progressData.usersProgress.map(user => {
    const moduleProgress = user.moduleProgress?.find(
      mp => mp.moduleId === moduleId || mp.moduleId?._id === moduleId
    );
    
    return {
      userName: `${user.user.first_name} ${user.user.last_name}`,
      progress: moduleProgress?.progress || 0,
      userId: user.userId || user.user._id
    };
  });

  // Compter les statuts de complétion
  const completed = moduleData.filter(user => user.progress === 100).length;
  const inProgress = moduleData.filter(user => user.progress > 0 && user.progress < 100).length;
  const notStarted = moduleData.filter(user => user.progress === 0).length;

  return {
    data: {
      labels: ['Complété', 'En progression', 'Non commencé'],
      datasets: [
        {
          data: [completed, inProgress, notStarted],
          backgroundColor: [
            'rgba(40, 167, 69, 0.7)',  // vert pour complété
            'rgba(255, 193, 7, 0.7)',  // jaune pour en progression
            'rgba(220, 53, 69, 0.7)'   // rouge pour non commencé
          ],
          borderColor: [
            '#28a745',
            '#ffc107',
            '#dc3545'
          ],
          borderWidth: 1
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: 'État de complétion du module',
          font: {
            size: 18,
            weight: 'bold'
          },
          padding: {
            top: 10,
            bottom: 15
          }
        },
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 15,
            padding: 15
          }
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.raw;
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = Math.round((value / total) * 100);
              return `${context.label}: ${value} étudiant${value > 1 ? 's' : ''} (${percentage}%)`;
            }
          }
        }
      }
    }
  };
};

// Configuration pour le graphique du meilleur score par etudiant
export const getQuizTopScoresChart = (quizData) => {
  if (!quizData || !quizData.topScores || quizData.topScores.length === 0) {
    return {
      data: {
        labels: [],
        datasets: [{
          type: "bar",
          label: "Score (%)",
          data: [],
          backgroundColor: "rgba(54, 162, 235, 0.7)"
        }]
      },
      options: {
        scales: {
          y: { beginAtZero: true, max: 100 }
        },
        plugins: {
          title: {
            display: true,
            text: "Meilleurs scores par étudiant"
          }
        }
      }
    };
  }

  const topScoresData = quizData.topScores;


  return {
    data: {
      labels: topScoresData.map(user => user.userName),
      datasets: [
        {
          type: "bar",
          label: "Score (%)",
          data: topScoresData.map(user => user.topScore),
          backgroundColor: "rgba(54, 162, 235, 0.7)",
          borderColor: "rgba(54, 162, 235, 1)",
          borderWidth: 1,
        },
        {
          type: "line",
          label: "Score moyen",
          data: Array(topScoresData.length).fill(quizData.averageScore || 0),
          borderColor: "rgba(255, 99, 132, 1)",
          borderWidth: 2,
          borderDash: [5, 5],
          fill: false,
          pointRadius: 0,
        }
      ],
    },
    options: {
      responsive: true,
      scales: {
        y: { 
          beginAtZero: true, 
          max: 100,
          title: {
            display: true,
            text: 'Score (%)'
          },
          ticks: {
            callback: value => `${value}%`
          }
        },
        x: {
          title: {
            display: true,
            text: 'Étudiants'
          }
        }
      },
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: `Meilleurs scores par étudiant`,
          font: {
            size: 18,
            weight: 'bold'
          },
          padding: {
            top: 10,
            bottom: 20
          }
        },
        legend: {
          display: true,
          position: 'bottom'
        },
        tooltip: {
          callbacks: {
            label: context => {
              if (context.dataset.type === 'line') {
                return `Score moyen: ${Math.round(context.raw)}%`;
              }
              return `Score: ${context.raw}%`;
            }
          }
        }
      }
    }
  };
};

// Configuration pour le graphique circulaire du taux de complétion du quiz
export const getQuizCompletionRateChart = (quizData) => {
  if (!quizData) {
    return {
      data: {
        labels: ['Aucune donnée'],
        datasets: [{
          data: [1],
          backgroundColor: ['rgba(200, 200, 200, 0.7)']
        }]
      },
      options: {
        plugins: {
          title: {
            display: true,
            text: 'Taux de complétion du quiz'
          }
        }
      }
    };
  }

  const completedAttempts = quizData.completedAttempts || 0;
  const abandonedAttempts = (quizData.totalAttempts || 0) - completedAttempts;

  return {
    data: {
      labels: ['Tentatives complétées', 'Tentatives abandonnées'],
      datasets: [
        {
          data: [completedAttempts, abandonedAttempts],
          backgroundColor: [
            'rgba(40, 167, 69, 0.7)',  // vert pour complété
            'rgba(220, 53, 69, 0.7)'   // rouge pour abandonné
          ],
          borderColor: [
            '#28a745',
            '#dc3545'
          ],
          borderWidth: 1
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: 'Taux de complétion du quiz',
          font: {
            size: 18,
            weight: 'bold'
          },
          padding: {
            top: 10,
            bottom: 15
          }
        },
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 15,
            padding: 15
          }
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.raw;
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
              return `${context.label}: ${value} (${percentage}%)`;
            }
          }
        },
        subtitle: {
          display: true,
          text: `Taux global de complétion: ${Math.round(quizData.completionRate * 100)}%`,
          padding: {
            bottom: 15
          }
        }
      }
    }
  };
};